import React, { useState, useEffect, useContext } from 'react';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import SendIcon from '@mui/icons-material/Send';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import { useGet, usePut } from '../../../sharedModules/hooks/useQuery';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import axios from '../../../sharedModules/utils/axios.utils';
import ChipMultiSelect from '../../../sharedModules/form/components/ChipMultiSelect';
import { normalizeAlertSettings } from '../utils/normalizeAlertSettings';
import {
  AlertSettings,
  DEFAULT_RECIPIENTS,
  DEFAULT_SETTINGS,
  FIELD_OPTIONS,
  FieldType,
  RecipientsState,
  SECTIONS,
  SectionKey,
  SectionRecipients,
  UserOption,
} from '../config/alertFields';

interface AlertSettingsResponse {
  result: {
    settings?: AlertSettings;
    recipients?: Record<string, any>;
  };
  admin_user_options?: UserOption[];
  salesperson_user_options?: UserOption[];
}

const sectionHeaderSx = {
  fontSize: '15px',
  fontWeight: 600,
  color: '#fff',
  bgcolor: '#3d5a80',
  px: 2,
  py: 1,
  borderRadius: '4px 4px 0 0',
};

const subHeaderSx = {
  fontSize: '13px',
  fontWeight: 600,
  color: '#3d5a80',
  bgcolor: '#e8eef4',
  px: 2,
  py: 0.75,
  borderRadius: 1,
  mb: 1,
  mt: 2,
};

const getOptionLabel = (value: string, fieldType: FieldType): string => {
  const opt = FIELD_OPTIONS[fieldType]?.find((o) => o.value === value);
  return opt?.label || value;
};

const getOptionColor = (value: string, fieldType: FieldType): string => {
  const opt = FIELD_OPTIONS[fieldType]?.find((o) => o.value === value);
  return opt?.color || '#999';
};

interface SectionRecipientsPanelProps {
  sectionKey: SectionKey;
  recipients: SectionRecipients;
  adminUserOptions: UserOption[];
  salespersonUserOptions: UserOption[];
  onChange: (sectionKey: SectionKey, updated: SectionRecipients) => void;
}

const SectionRecipientsPanel: React.FC<SectionRecipientsPanelProps> = ({
  sectionKey,
  recipients,
  adminUserOptions,
  salespersonUserOptions,
  onChange,
}) => {
  const updateField = (field: keyof SectionRecipients, value: any) => {
    onChange(sectionKey, { ...recipients, [field]: value });
  };

  const handleAdminUsersChange = (value: number[]) => {
    updateField('admin_user_ids', value);
  };

  const handleSalespersonUsersChange = (value: number[]) => {
    updateField('salesperson_user_ids', value);
  };

  const renderUserSelect = (
    label: string,
    value: number[],
    options: UserOption[],
    emptyMessage: string,
    onChange: (value: number[]) => void,
  ) =>
    options.length === 0 ? (
      <FormControl fullWidth size="small">
        <InputLabel>{label}</InputLabel>
        <Select disabled>
          <MenuItem disabled>{emptyMessage}</MenuItem>
        </Select>
      </FormControl>
    ) : (
      <ChipMultiSelect<number>
        label={label}
        options={options}
        value={value}
        onChange={onChange}
        variant="outlined"
        fullWidth
        size="small"
        showCheckboxes
      />
    );

  return (
    <>
      <Typography sx={subHeaderSx}>Admin Recipients</Typography>
      <Box sx={{ px: 1, mb: 1 }}>
        {renderUserSelect(
          'Admin Users',
          recipients.admin_user_ids,
          adminUserOptions,
          'No admin users found.',
          handleAdminUsersChange,
        )}
      </Box>

      <Typography sx={subHeaderSx}>Salesperson Notifications</Typography>
      <Box sx={{ px: 1, mb: 1 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={recipients.notify_salespeople}
              onChange={(e) => updateField('notify_salespeople', e.target.checked)}
            />
          }
          label={<Typography variant="body2">Notify Salespeople</Typography>}
        />
        {recipients.notify_salespeople && (
          <Box sx={{ mt: 1 }}>
            {renderUserSelect(
              'Salespeople',
              recipients.salesperson_user_ids,
              salespersonUserOptions,
              'No salesperson users found.',
              handleSalespersonUsersChange,
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

const AlertNotificationPage: React.FC = () => {
  const { showSuccessSnackBar, showErrorSnackBar } = useContext(SnackBarContext);
  const [settings, setSettings] = useState<AlertSettings>(DEFAULT_SETTINGS);
  const [recipients, setRecipients] = useState<RecipientsState>(DEFAULT_RECIPIENTS);
  const [adminUserOptions, setAdminUserOptions] = useState<UserOption[]>([]);
  const [salespersonUserOptions, setSalespersonUserOptions] = useState<UserOption[]>([]);
  const [previewEmail, setPreviewEmail] = useState('');
  const [sendingPreview, setSendingPreview] = useState(false);

  const {
    data: response,
    isLoading,
    error,
  } = useGet<AlertSettingsResponse>({
    url: '/home_services/inspection_alert_notification_settings',
  });

  const { mutateAsync, isPending } = usePut();

  useEffect(() => {
    const data = response?.data;
    if (!data) return;

    const result = data.result;
    if (result) {
      const normalized = normalizeAlertSettings(result);
      setSettings(normalized.settings);
      setRecipients(normalized.recipients);
    }

    if (data.admin_user_options) setAdminUserOptions(data.admin_user_options);
    if (data.salesperson_user_options) setSalespersonUserOptions(data.salesperson_user_options);
  }, [response]);

  const handleChange = (key: string, event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSettings((prev) => ({
      ...prev,
      [key]: typeof value === 'string' ? value.split(',') : value,
    }));
  };

  const handleRecipientsChange = (sectionKey: SectionKey, updated: SectionRecipients) => {
    setRecipients((prev) => ({ ...prev, [sectionKey]: updated }));
  };

  const handleSave = async () => {
    try {
      await mutateAsync({
        url: '/home_services/inspection_alert_notification_settings',
        data: {
          inspection_alert_notification_settings: settings,
          recipients,
        },
      });
      showSuccessSnackBar?.('Alert settings saved.');
    } catch {
      showErrorSnackBar?.('Failed to save alert settings.');
    }
  };

  const handleSendPreview = async () => {
    if (!previewEmail) return;
    setSendingPreview(true);
    try {
      await axios.post('/home_services/inspection_alert_notification_settings/preview', { email: previewEmail });
      showSuccessSnackBar?.(`Preview alert sent to ${previewEmail}`);
    } catch {
      showErrorSnackBar?.('Failed to send preview alert email.');
    } finally {
      setSendingPreview(false);
    }
  };

  const loading = isLoading || isPending;
  const errorMessage = error ? 'Failed to load alert settings.' : '';

  return (
    <LoadingWrapper isLoading={loading} message="Loading...">
      <Grid container spacing={3} style={{ padding: '1% 2%' }}>
        <Grid item xs={12}>
          <PageHeader mainTitle="Home Services" subTitle="Alert Notifications" />
        </Grid>

        {errorMessage && (
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>
        )}

        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              mb: 1,
            }}
          >
            <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
              Configure which inspection report values trigger an alert email, and who should receive those alerts per
              section.
            </Typography>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} sx={{ flexShrink: 0 }}>
              Save Settings
            </Button>
          </Box>
        </Grid>

        {SECTIONS.map((section) => (
          <Grid item xs={12} md={6} key={section.title}>
            <Paper style={{ overflow: 'hidden' }}>
              <Typography sx={sectionHeaderSx}>{section.title}</Typography>
              <Grid container spacing={2} style={{ padding: 16 }}>
                <Grid item xs={12}>
                  <SectionRecipientsPanel
                    sectionKey={section.sectionKey}
                    recipients={recipients[section.sectionKey]}
                    adminUserOptions={adminUserOptions}
                    salespersonUserOptions={salespersonUserOptions}
                    onChange={handleRecipientsChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="overline" color="textSecondary" display="block" sx={{ lineHeight: 1.5 }}>
                    Alert Triggers
                  </Typography>
                </Grid>
                {section.attributes.map(({ key, label, type }) => {
                  const options = FIELD_OPTIONS[type];
                  const selected = settings[key] || [];

                  return (
                    <Grid item xs={12} key={key}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{label}</InputLabel>
                        <Select
                          multiple
                          value={selected}
                          label={label}
                          onChange={(e) => handleChange(key, e)}
                          renderValue={(sel) => {
                            const vals = sel as string[];
                            if (vals.length === 0) return 'No Alert';
                            return (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {vals.map((v) => (
                                  <Chip
                                    key={v}
                                    label={getOptionLabel(v, type)}
                                    size="small"
                                    sx={{
                                      bgcolor: getOptionColor(v, type),
                                      color: '#fff',
                                      fontWeight: 600,
                                      fontSize: '11px',
                                    }}
                                  />
                                ))}
                              </Box>
                            );
                          }}
                        >
                          {options.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              <Checkbox checked={selected.includes(opt.value)} />
                              <ListItemText primary={opt.label} />
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: opt.color,
                                  ml: 1,
                                  flexShrink: 0,
                                }}
                              />
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={loading}>
            Save Settings
          </Button>
        </Grid>

        {/* Send Preview */}
        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        <Grid item xs={12}>
          <Paper style={{ padding: 24 }}>
            <Typography variant="h6" gutterBottom>
              Send Preview
            </Typography>
            <Divider style={{ marginBottom: 16 }} />
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Send a sample alert email using the most recent inspection report to any email address.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Email Address"
                value={previewEmail}
                onChange={(e) => setPreviewEmail(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
              />
              <Button
                variant="contained"
                startIcon={<SendIcon />}
                onClick={handleSendPreview}
                disabled={sendingPreview || !previewEmail}
              >
                {sendingPreview ? 'Sending...' : 'Send Preview'}
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default AlertNotificationPage;
